import { useEffect, useMemo, useRef, useState } from 'react';
import { Divider, Input, Select, Spin, Typography, message } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import TipTapEditor from '../TipTapEditor';
import { getModules } from '../../services/adminApi';
import { getRuntimeEmailTemplate, getRuntimeEmailTemplates } from '../../services/emailTemplateApi';
import { parseEmails } from './mailUtils';

const { Text } = Typography;

function mergeEmails(current, incoming) {
  return [...new Set([...parseEmails(current), ...parseEmails(incoming)])].join(', ');
}

function nestedValue(record, path) {
  return String(path ?? '').split('.').reduce((value, key) => value?.[key], record);
}

function resolveTemplateText(text, placeholders, record) {
  if (!record) return String(text ?? '');
  const values = new Map();
  (placeholders ?? []).forEach((group) => (group.holders ?? []).forEach((holder) => {
    values.set(holder.key, nestedValue(record, holder.value));
  }));
  return String(text ?? '').replace(/{{\.([\w.]+)}}/g, (token, key) => {
    const value = values.get(key);
    return value === undefined || value === null ? token : String(value);
  });
}

export default function CommonMailComposer({
  value,
  onChange,
  moduleName = '',
  defaultTemplateId = '',
  ready = true,
  title = 'Mail',
  hint,
  bccHint,
  afterBcc,
  className = '',
  contextRecords = [],
}) {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(moduleName);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const appliedDefault = useRef('');

  const patch = (next) => onChange?.({ ...value, ...next });

  useEffect(() => {
    getModules().then((items) => setModules(items.map((item) => ({
      value: item.key ?? item.module ?? item.value,
      label: item.label ?? item.name ?? item.key ?? item.module ?? item.value,
    })).filter((item) => item.value))).catch(() => setModules([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    getRuntimeEmailTemplates({ module: selectedModule })
      .then((items) => { if (!cancelled) setTemplates(items); })
      .catch(() => { if (!cancelled) setTemplates([]); })
      .finally(() => { if (!cancelled) setLoadingTemplates(false); });
    return () => { cancelled = true; };
  }, [selectedModule]);

  async function applyTemplate(id, quiet = false) {
    if (!id) { setSelectedTemplate(''); return; }
    try {
      const template = await getRuntimeEmailTemplate(id);
      const record = contextRecords[0];
      setSelectedTemplate(id);
      patch({
        to: mergeEmails(value.to, (template?.toMail ?? []).map((email) => resolveTemplateText(email, template?.placeholders, record))),
        cc: mergeEmails(value.cc, (template?.ccMail ?? []).map((email) => resolveTemplateText(email, template?.placeholders, record))),
        bcc: mergeEmails(value.bcc, (template?.bccMail ?? []).map((email) => resolveTemplateText(email, template?.placeholders, record))),
        subject: resolveTemplateText(template?.subject, template?.placeholders, record),
        contents: resolveTemplateText(template?.body, template?.placeholders, record),
      });
      if (!quiet) message.success(`Loaded ${template?.name || 'email template'}`);
    } catch (error) {
      if (!quiet) message.error(error?.message || 'Could not load email template');
    }
  }

  useEffect(() => {
    if (!ready || !defaultTemplateId || appliedDefault.current === defaultTemplateId) return;
    appliedDefault.current = defaultTemplateId;
    applyTemplate(defaultTemplateId, true);
    // Apply once after the popup's record defaults have loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTemplateId, ready]);

  const moduleOptions = useMemo(() => {
    if (!selectedModule || modules.some((item) => item.value === selectedModule)) return modules;
    return [{ value: selectedModule, label: selectedModule }, ...modules];
  }, [modules, selectedModule]);

  return (
    <div className={`common-mail-composer ${className}`.trim()}>
      <div className="mail-popup__header">
        <div className="mail-popup__header-brand">
          <span className="dynamic-popup__header-icon-box">
            <MailOutlined />
          </span>
          <div>
            <span className="dynamic-popup__header-label">{title}</span>
            {hint && <Text type="secondary" className="mail-popup__header-hint">{hint}</Text>}
          </div>
        </div>
        <div className="mail-popup__header-controls">
          <Select showSearch optionFilterProp="label" placeholder="Module" value={selectedModule || undefined} options={moduleOptions} onChange={(module) => { setLoadingTemplates(true); setSelectedModule(module); setSelectedTemplate(''); patch({ cc: '', bcc: '', subject: '', contents: '', showCc: false, showBcc: false }); }} className="mail-popup__module-select" />
          <Select
            showSearch optionFilterProp="label" allowClear placeholder="Template"
            value={selectedTemplate || undefined}
            loading={loadingTemplates}
            notFoundContent={loadingTemplates ? <Spin size="small" /> : 'No templates'}
            options={templates.map((item) => ({ value: item._id ?? item.id, label: item.name || item.action || 'Template' }))}
            onChange={(id) => applyTemplate(id)}
            className="mail-popup__template-select"
          />
        </div>
      </div>

      <div className="mail-popup__body">
        <div className="mail-popup__field-row">
          <Text type="secondary" className="mail-popup__field-label">To</Text>
          <Select mode="tags" open={false} tokenSeparators={[',']} value={parseEmails(value.to)} onChange={(items) => patch({ to: items.join(', ') })} variant="borderless" className="mail-popup__recipient-select" placeholder="Add email addresses" />
          <div className="mail-popup__cc-bcc-actions">
            {!value.showCc && <span className="mail-popup__cc-bcc-link" onClick={() => patch({ showCc: true })}>Cc</span>}
            {!value.showBcc && <span className="mail-popup__cc-bcc-link" onClick={() => patch({ showBcc: true })}>Bcc</span>}
          </div>
        </div>
        <Divider />
        {value.showCc && <><div className="mail-popup__field-row"><Text type="secondary" className="mail-popup__field-label">Cc</Text><Select mode="tags" open={false} tokenSeparators={[',']} value={parseEmails(value.cc)} onChange={(items) => patch({ cc: items.join(', ') })} variant="borderless" className="mail-popup__recipient-select" /></div><Divider /></>}
        {value.showBcc && <><div className="mail-popup__field-row"><Text type="secondary" className="mail-popup__field-label">Bcc</Text><Select mode="tags" open={false} tokenSeparators={[',']} value={parseEmails(value.bcc)} onChange={(items) => patch({ bcc: items.join(', ') })} variant="borderless" className="mail-popup__recipient-select" placeholder={bccHint} /></div>{afterBcc}<Divider /></>}
        <div className="mail-popup__field-row"><Text type="secondary" className="mail-popup__field-label">Subject</Text><Input variant="borderless" value={value.subject} onChange={(event) => patch({ subject: event.target.value })} placeholder="Enter subject" className="mail-popup__field-input" /></div>
        <Divider />
        <div className="mail-popup__message-section"><Text type="secondary" className="mail-popup__message-label">Message</Text><TipTapEditor value={value.contents} onChange={(contents) => patch({ contents })}/></div>
      </div>
    </div>
  );
}
